import SearchIcon from "@mui/icons-material/Search";
import React, { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { ReactComponent as DeleteIcon } from "../../../assets/images/delete-icon.svg";
import {
  Column,
  FormController,
  MuiBox,
  MuiButtonSecondary,
  MuiGrid,
  MuiIconButton,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  OptionsDataType,
} from "../../../components";
import { CurrencyFormat } from "../../../components/textFieldCurrency/CurrencyUtils";
import { TextFieldCurrency } from "../../../components/textFieldCurrency/TextFieldCurrency";
import { commonSiteCoreKeys } from "../../../helpers/constants/siteCoreKeys";
import {
  calculateTotalTaxAmount,
  getFormattedAmount,
  getValueByPercentage,
  getValueWith2DecimalDigits,
  isEmpty,
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../helpers/utils";
import { useSiteCore } from "../../../hooks/useSiteCore";

import {
  TaxConfigureTableData,
  TaxDetailsFormKeyProps,
} from "../modelsAndSchemas/Models";

import { InputAdornment } from "@mui/material";
import { TaxType } from "../../../api/services/program/models";
import { EMPTY_VALUE_DASH, MAX_LENGTH_50 } from "../../../helpers/constants";
import { useGridContainer } from "../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../hooks/useNavigationSiteCore";
import { TaxDetailsTableWrapper } from "./TaxDetailsTable.style";
import { TaxDetailsTableHeader } from "./taxDetailsTableHeader/TaxDetailsTableHeader";

interface TaxDetailsTableProps {
  pageId: string;
  currencyName: string;
  currencyFormat: CurrencyFormat;
  currencyId: string;
  addTaxConfiguration: () => void;
  filteredResults: TaxConfigureTableData[];
  onDeleteTaxConfigurationEntryAction: (row: TaxConfigureTableData) => void;
  onTaxAddedCallback: boolean;
  taxList?: TaxType[];
  onTaxDeletedCallback: boolean;
  taxDetailsFormKeys: TaxDetailsFormKeyProps;
  programFee: string;
  isInViewMode?: boolean;
  hasDeletePermission?: boolean;
}

export const TaxDetailsTable: React.FC<TaxDetailsTableProps> = ({
  currencyFormat,
  currencyId,
  currencyName,
  addTaxConfiguration,
  filteredResults,
  onDeleteTaxConfigurationEntryAction,
  onTaxAddedCallback,
  taxList,
  onTaxDeletedCallback,
  pageId,
  taxDetailsFormKeys,
  programFee,
  isInViewMode = false,
  hasDeletePermission = true,
}) => {
  const { gridSizeMedium: gridSize } = useGridContainer();

  const { control, setValue, getValues, watch } = useFormContext();
  const { commonLabels, fetchValidationMessage, commonTaxDetails } =
    useSiteCore();

  const { commonARIALabel } = useNavigationSiteCore();

  const {
    taxKey,
    taxPercentageKey,
    taxAmountWithFormatKey,
    taxAmountKey,
    taxSearchKey,
  } = taxDetailsFormKeys;

  const {
    addTaxHeading,
    tax,
    percentage,
    amount: amountLabel,
    totalTaxAmount,
    itemDescriptionTableColumn,
    percentageTableColumn,
    amountTableColumn,
    actionTableColumn,
  } = commonTaxDetails;
  const [taxDropdownData, setTaxDropdownData] = useState<OptionsDataType[]>([]);
  const [filteredResultsLocal, setFilteredResultsLocal] = useState<
    TaxConfigureTableData[]
  >([...filteredResults]);
  const tableRef = useRef<{ resetPagination: () => void }>(null);

  const onTaxChange = (selectedValue: string) => {
    const getTaxData = taxList?.find((tax) => tax.id === selectedValue);

    if (isNotEmpty(getTaxData)) {
      const taxPercentageValue = getTaxData?.percentage ?? 0;
      const taxAmountValue = getValueWith2DecimalDigits(
        getValueByPercentage(programFee, taxPercentageValue).toString(),
      );

      setValue(taxPercentageKey, taxPercentageValue);
      setValue(taxAmountWithFormatKey, taxAmountValue);
      setValue(taxAmountKey, taxAmountValue);
    } else {
      setValue(taxPercentageKey, EMPTY_VALUE_DASH);
      setValue(taxAmountWithFormatKey, EMPTY_VALUE_DASH);
      setValue(taxAmountKey, EMPTY_VALUE_DASH);
    }
  };

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  useEffect(() => {
    setFilteredResultsLocal([...filteredResults]);
    resetPagination();
  }, [filteredResults]);

  useEffect(() => {
    const { taxSearch } = getValues();

    const filteredTax = [...filteredResults].filter((item) => {
      const matchesTaxName = item.description
        .toLowerCase()
        .includes(taxSearch?.toLowerCase());

      return matchesTaxName;
    });

    setFilteredResultsLocal([...filteredTax]);
    resetPagination();
  }, [watch(taxSearchKey)]);

  useEffect(() => {
    setTaxDropdownData(setApiDataToDropDownOption(taxList ?? []));
  }, [taxList]);

  const updateAllTaxConfig = () => {
    updateTaxDropdownData();
  };

  useEffect(() => {
    updateAllTaxConfig();
  }, [onTaxAddedCallback, onTaxDeletedCallback, filteredResults]);

  const updateTaxDropdownData = () => {
    const filteredResultsCopy = [...filteredResults];

    const uniqueTax = (taxList ?? []).filter((tax) => {
      return !filteredResultsCopy.some((result) =>
        result.taxTypeId?.includes(tax.id),
      );
    });

    setTaxDropdownData(setApiDataToDropDownOption(uniqueTax ?? []));
  };

  const getTaxTotalAmount = () => {
    const totalAmountOrPercentage = calculateTotalTaxAmount(filteredResults);
    return getFormattedAmount(totalAmountOrPercentage, currencyFormat?.locale);
  };

  const tableHeaderForm = [
    <FormController key={taxSearchKey} control={control} name={taxSearchKey}>
      <MuiTextField
        id={`${pageId}-${taxSearchKey}`}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        aria-label={commonLabels.search}
      />
    </FormController>,
    <></>,
    <></>,
    <></>,
  ];

  const columns: Column[] = [
    {
      id: taxKey,
      label: itemDescriptionTableColumn,
      isSortVisible: false,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">{row.description}</MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: taxPercentageKey,
      label: percentageTableColumn,
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">{`${row.percentage}%`}</MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: taxAmountWithFormatKey,
      label: amountTableColumn,
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {`${currencyName} ${row.amountWithFormat}`}
          </MuiTypography>
        </MuiBox>
      ),
    },
    ...(!isInViewMode
      ? [
          {
            id: "action",
            label: actionTableColumn,
            render: (row: any) => {
              return hasDeletePermission ? (
                <MuiIconButton
                  onClick={() => onDeleteTaxConfigurationEntryAction(row)}
                  data-testid={`${pageId}-delete`}
                  aria-label={commonARIALabel.delete}
                >
                  <DeleteIcon />
                </MuiIconButton>
              ) : null;
            },
          },
        ]
      : []),
  ];

  return (
    <TaxDetailsTableWrapper>
      {!isInViewMode ? (
        <>
          <TaxDetailsTableHeader header={addTaxHeading} />
          <MuiBox className="tax-configuration-middle">
            <MuiGrid container spacing={2}>
              <MuiGrid size={gridSize}>
                <FormController control={control} name={taxKey}>
                  <MuiSelect
                    id={`${pageId}-${taxKey}`}
                    label={tax}
                    showSearch
                    options={taxDropdownData}
                    visibleDefaultSelectOption={true}
                    placeholder={commonLabels.selectPlaceHolder}
                    onChange={(event) => {
                      onTaxChange(event.target.value as string);
                    }}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <FormController control={control} name={taxPercentageKey}>
                  <TextFieldCurrency
                    id={`${pageId}-${taxPercentageKey}`}
                    formId={taxPercentageKey}
                    locale={currencyFormat?.locale}
                    useGrouping={false}
                    isCurrencyChanged={currencyId}
                    zeroValidationMessage={fetchValidationMessage(
                      commonSiteCoreKeys.greaterThanZeroFieldValidator,
                    )}
                    formatErrorMessage={fetchValidationMessage(
                      commonSiteCoreKeys.alphabetNotAllowedValidator,
                    )}
                    name={taxPercentageKey}
                    label={percentage}
                    placeholder={EMPTY_VALUE_DASH}
                    getFinalValues={(value, _valueNonFormatted) => {
                      setValue(taxPercentageKey, value);
                    }}
                    percentRangeErrorMessage={fetchValidationMessage(
                      commonSiteCoreKeys.pleaseEnterNumberBetween1To100,
                    )}
                    allowDecimal={false}
                    isForPercentageUsage
                    disabled
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>

              <MuiGrid size={gridSize}>
                <FormController control={control} name={taxAmountWithFormatKey}>
                  <TextFieldCurrency
                    id={`${pageId}-${taxAmountWithFormatKey}`}
                    formId={taxAmountWithFormatKey}
                    locale={currencyFormat?.locale}
                    isCurrencyChanged={currencyId}
                    zeroValidationMessage={fetchValidationMessage(
                      commonSiteCoreKeys.greaterThanZeroFieldValidator,
                    )}
                    formatErrorMessage={fetchValidationMessage(
                      commonSiteCoreKeys.alphabetNotAllowedValidator,
                    )}
                    name={`${taxAmountWithFormatKey}`}
                    label={`${amountLabel} (${currencyName})`}
                    placeholder={EMPTY_VALUE_DASH}
                    getFinalValues={(value, valueNonFormatted) => {
                      setValue(taxAmountKey, valueNonFormatted);
                      setValue(taxAmountWithFormatKey, value);
                    }}
                    disabled
                    showDoubleDash={isEmpty(getValues(taxKey))}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>
            </MuiGrid>

            <MuiBox className="tax-configuration-add-button">
              <MuiButtonSecondary
                onClick={addTaxConfiguration}
                data-testId={`${pageId}-add-tax`}
              >
                {commonLabels.add}
              </MuiButtonSecondary>
            </MuiBox>
          </MuiBox>
        </>
      ) : null}
      <MuiBox className="tax-configuration-total-amount-percentage-container">
        <MuiTypography variant="h3Bold">{totalTaxAmount}</MuiTypography>

        <MuiGrid className="tax-configuration-total-amount-percentage">
          <MuiTypography variant="h3Bold">
            {`${currencyName} ${getTaxTotalAmount()}`}
          </MuiTypography>
        </MuiGrid>
      </MuiBox>
      <MuiBox className="tax-configuration-table">
        <MuiTable
          ref={tableRef}
          pageId={pageId}
          tableHeaderForm={tableHeaderForm}
          columns={columns}
          tableResults={[...filteredResultsLocal]}
          noDataInApi={filteredResults.length === 0}
          noResultMessage={commonLabels.noResult}
          emptyMessage={
            commonTaxDetails.noTaxAvailablePleaseAddTheRequiredTaxDetailsManually
          }
          priorityResults={[]}
        />
      </MuiBox>
    </TaxDetailsTableWrapper>
  );
};
